import React, { useState } from "react"
import { motion } from "framer-motion"
import {
    HiOutlineEye,
    HiOutlinePencilSquare,
    HiOutlineKey,
    HiOutlinePhoto,
    HiOutlineDocumentArrowDown,
    HiOutlineClipboardDocument,
    HiOutlineEllipsisVertical,
    HiOutlineCheckCircle,
    HiOutlineXCircle,
    HiOutlineArrowPathRoundedSquare,
    HiOutlineTrash,
    HiOutlineCreditCard,
} from "react-icons/hi2"
import { RiWhatsappLine } from "react-icons/ri"
import dummy from "../../Assets/Images/dummyProfile.jpeg"

const VERIFICATION_LABEL = {
    0: "Pending",
    1: "Approved",
    2: "Declined",
    3: "Inactive",
    4: "Happy",
    5: "Blocked",
    6: "Under Review",
}

const statusStyle = (status) => {
    const v = String(status)
    if (v === "1" || v === "Approved") return "bg-[#ecf8ef] text-[#1a8a3a]"
    if (v === "2" || v === "Declined") return "bg-[#fff5f5] text-[#a03e3e]"
    if (v === "6" || v === "Under Review") return "bg-[#fff8e6] text-[#a07414]"
    if (v === "4" || v === "Married") return "bg-[#f1efff] text-[#5947b6]"
    return "bg-slate-100 text-slate-600"
}

// Lightweight profile-completeness measure: counts populated fields the app cares about.
const completeness = (u) => {
    const keys = [
        "firstName", "lastName", "gender", "dateOfBirth", "religion", "caste",
        "motherTongue", "height", "occupation", "education", "starName", "rasi",
        "address", "city", "state", "fatherName", "motherName", "aboutMe",
    ]
    let filled = 0
    keys.forEach((k) => {
        const v = u?.[k] ?? u?.contactInformation?.[k] ?? u?.familyInformation?.[k]
        if (v !== undefined && v !== null && String(v).trim() !== "") filled += 1
    })
    return Math.round((filled / keys.length) * 100)
}

const cleanMobile = (m) => String(m || "").replace(/\D/g, "")

const formatName = (u) => {
    const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim()
    return name || u?.refId || "Member"
}

const primaryImage = (u) => {
    const images = u?.images || u?.previewUrls || []
    const first = images.find((i) => i?.imageUrl)?.imageUrl || images[0]?.imageUrl
    return first || dummy
}

const ActionPill = ({ icon: Icon, label, color = "default", onClick, danger }) => {
    const palette = {
        default: "border-borderColor bg-white text-tColor hover:border-primeColor hover:text-primeColor",
        whatsapp: "border-[#cfe9d6] bg-[#eef9f1] text-[#1f7a3d] hover:border-[#1f7a3d]",
        pdf: "border-[#e6d8b8] bg-[#fdf6e3] text-[#8b6a14] hover:border-[#8b6a14]",
        primary: "border-primeColor bg-primeColor text-white",
    }
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                danger ? "border-[#efcaca] bg-[#fff5f5] text-[#a03e3e]" : palette[color] || palette.default
            }`}
        >
            <Icon className="h-4 w-4" />
            {label}
        </button>
    )
}

const isMarried = (user) => {
    const s = user?.status
    const sStr = String(s ?? '').toLowerCase()
    return s === 4 || sStr === 'happy customer' || sStr === 'married'
}

const UserMobileCard = ({ user, actions, onCopyMobile }) => {
    const [moreOpen, setMoreOpen] = useState(false)
    const completion = completeness(user)
    const verifLabel = VERIFICATION_LABEL[user?.status] || (typeof user?.status === "string" ? user.status : "Pending")
    const mobileDigits = cleanMobile(user?.contactInformation?.mobileNumber || user?.mobileNumber)
    const whatsappHref = mobileDigits ? `https://wa.me/${mobileDigits.length === 10 ? `91${mobileDigits}` : mobileDigits}` : null

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-borderColor bg-white p-4 shadow-sm"
        >
            <div className="flex items-start gap-3">
                <img
                    src={primaryImage(user)}
                    alt={formatName(user)}
                    className="h-14 w-14 flex-shrink-0 rounded-2xl object-cover ring-1 ring-borderColor"
                    loading="lazy"
                />
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="truncate text-base font-semibold text-tColor">{formatName(user)}</p>
                            <p className="truncate text-xs font-medium text-tpColor">{user?.refId || "—"}</p>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${statusStyle(user?.status)}`}>
                            {verifLabel}
                        </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                        <button
                            type="button"
                            onClick={() => onCopyMobile?.(mobileDigits)}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 font-semibold text-tColor"
                            title="Copy mobile"
                        >
                            <HiOutlineClipboardDocument className="h-3.5 w-3.5" />
                            {mobileDigits || "No mobile"}
                        </button>
                        {user?.gender && (
                            <span className="rounded-full bg-slate-100 px-2 py-1 font-semibold capitalize text-tColor">{user.gender}</span>
                        )}
                        {(user?.status === 4 || user?.status === "Married" || user?.happyCustomer) ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-600">
                                Closed
                            </span>
                        ) : (
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${user?.activeStat === "A" ? "bg-[#ecf8ef] text-[#1a8a3a]" : "bg-[#fff5f5] text-[#a03e3e]"}`}>
                                {user?.activeStat === "A" ? "Active" : "Deactivated"}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.16em] text-tpColor">
                    <span>Profile {completion}%</span>
                    <span>{user?.starName || user?.star || ""}</span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                        className="h-full rounded-full bg-primeColor transition-all"
                        style={{ width: `${completion}%` }}
                    />
                </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
                <ActionPill icon={HiOutlineEye} label="View" onClick={() => actions.view?.(user)} />
                {!isMarried(user) && whatsappHref && (
                    <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-full border border-[#cfe9d6] bg-[#eef9f1] px-3 py-1.5 text-xs font-bold text-[#1f7a3d] hover:border-[#1f7a3d]"
                    >
                        <RiWhatsappLine className="h-4 w-4" />
                        Direct
                    </a>
                )}
                {!isMarried(user) && <ActionPill icon={RiWhatsappLine} label="Share" color="whatsapp" onClick={() => actions.whatsappShare?.(user)} />}
                <ActionPill icon={HiOutlineDocumentArrowDown} label="PDF" color="pdf" onClick={() => actions.pdf?.(user)} />
                <button
                    type="button"
                    onClick={() => setMoreOpen((o) => !o)}
                    aria-label="More actions"
                    className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-borderColor bg-white text-tColor hover:border-primeColor hover:text-primeColor"
                >
                    <HiOutlineEllipsisVertical className="h-4 w-4" />
                </button>
            </div>

            {moreOpen && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                    <ActionPill icon={HiOutlinePencilSquare} label="Edit" onClick={() => actions.edit?.(user)} />
                    {!isMarried(user) && <ActionPill icon={HiOutlineKey} label="Password" onClick={() => actions.changePassword?.(user)} />}
                    <ActionPill icon={HiOutlinePhoto} label="Images" onClick={() => actions.manageImages?.(user)} />
                    {!isMarried(user) && <ActionPill icon={RiWhatsappLine} label="Match Profiles" onClick={() => actions.sendMatching?.(user)} />}
                    {!isMarried(user) && <ActionPill icon={HiOutlineCreditCard} label="Activate Plan" onClick={() => actions.activatePlan?.(user)} />}
                    <ActionPill icon={HiOutlineEye} label="Portal" onClick={() => actions.preview?.(user)} />
                    {!isMarried(user) && <ActionPill icon={HiOutlineCheckCircle} label="Approve" onClick={() => actions.approve?.(user)} />}
                    {!isMarried(user) && <ActionPill icon={HiOutlineXCircle} label="Decline" onClick={() => actions.decline?.(user)} />}
                    {!isMarried(user) && (user?.activeStat === "A" ? (
                        <ActionPill icon={HiOutlineTrash} label="Deactivate" danger onClick={() => actions.deactivate?.(user)} />
                    ) : (
                        <ActionPill icon={HiOutlineArrowPathRoundedSquare} label="Activate" onClick={() => actions.activate?.(user)} />
                    ))}
                </div>
            )}
        </motion.div>
    )
}

const UsersMobileList = ({ users, actions, onCopyMobile }) => {
    if (!users || users.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-borderColor bg-lightGrey/30 p-6 text-center text-sm text-tpColor">
                There are no records to display.
            </div>
        )
    }
    return (
        <div className="grid gap-3">
            {users.map((u) => (
                <UserMobileCard
                    key={u._id}
                    user={u}
                    actions={actions}
                    onCopyMobile={onCopyMobile}
                />
            ))}
        </div>
    )
}

export default UsersMobileList
